// ─── addToCalendarUtils.ts ────────────────────────────────────────────────────
// Utility functions to generate calendar links and ICS file content.
// Supports: Google Calendar, Outlook Web, Apple Calendar / Outlook Desktop (ICS)

export interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  startDate: Date;
  endDate: Date;
  /** Optional: organizer email shown in the ICS invite */
  organizerEmail?: string;
  /** Optional: organizer name shown in the ICS invite */
  organizerName?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Format a Date to Google/Yahoo/Outlook URL param format: YYYYMMDDTHHmmssZ */
function toGoogleDateString(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/** Format a Date to Outlook Web ISO format: YYYY-MM-DDTHH:mm:ss */
function toOutlookDateString(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, "");
}

/** Format a Date to ICS format: YYYYMMDDTHHmmssZ */
function toICSDateString(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/** Escape special characters for ICS text fields */
function escapeICS(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

/** Generate a unique UID for the ICS event */
function generateUID(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}@calendar`;
}

// ── Google Calendar ───────────────────────────────────────────────────────────

/**
 * Returns a Google Calendar "Add Event" URL.
 * Opens directly in the user's Google Calendar in a new tab.
 */
export function getGoogleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${toGoogleDateString(event.startDate)}/${toGoogleDateString(event.endDate)}`,
    ...(event.description && { details: event.description }),
    ...(event.location && { location: event.location }),
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// ── Outlook Web ───────────────────────────────────────────────────────────────

/**
 * Returns an Outlook Web "Add Event" URL.
 * Opens directly in Outlook.live.com in a new tab.
 */
export function getOutlookWebUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    startdt: toOutlookDateString(event.startDate),
    enddt: toOutlookDateString(event.endDate),
    subject: event.title,
    ...(event.description && { body: event.description }),
    ...(event.location && { location: event.location }),
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

// ── ICS File (Apple Calendar, Outlook Desktop, any calendar app) ─────────────

/**
 * Returns the raw ICS file content as a string.
 * Compatible with Apple Calendar, Outlook Desktop, Thunderbird, and any
 * app that supports the iCalendar standard (RFC 5545).
 */
export function generateICSContent(event: CalendarEvent): string {
  const uid = generateUID();
  const now = toICSDateString(new Date());

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Conference Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${toICSDateString(event.startDate)}`,
    `DTEND:${toICSDateString(event.endDate)}`,
    `SUMMARY:${escapeICS(event.title)}`,
    event.description ? `DESCRIPTION:${escapeICS(event.description)}` : "",
    event.location ? `LOCATION:${escapeICS(event.location)}` : "",
    event.organizerEmail
      ? `ORGANIZER;CN=${event.organizerName ?? event.organizerEmail}:mailto:${event.organizerEmail}`
      : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean) // remove empty lines from optional fields
    .join("\r\n"); // ICS spec requires CRLF line endings

  return lines;
}

/**
 * Triggers an `.ics` file download in the browser.
 * Works for Apple Calendar, Outlook Desktop, and any calendar app.
 */
export function downloadICSFile(event: CalendarEvent): void {
  const content = generateICSContent(event);
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${event.title.replace(/\s+/g, "_")}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
